import * as React from 'react'

import { Box, Button, Href, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { ContractFlowType } from 'modules/contracts/components/FlowPage'
import FormWrapper from 'modules/contracts/components/FlowPage/FormWrapper'
import Hint from 'modules/contracts/components/FlowPage/FormWrapper/Hint'
import { useContractFlow } from 'modules/contracts/lib/contractFlowContext'
import { translate } from 'modules/contracts/lib/translate'
import ContractVersionPayrollPolicy from 'modules/contracts/store/models/ContractVersionPayrollPolicy'
import { usePolicies } from 'modules/payroll_bundle/store/collections/policies'

import AssignPolicyModal from './AssignPolicyModal'
import Item from './Item'

interface Props {
  translations: { [key: string]: string }
  canEdit: boolean
  step?: number
  type: ContractFlowType
  withFormWrapper?: boolean
}

interface PayrollPolicyHintProps {
  step: number
  type: ContractFlowType
}

const PayrollPolicyHint = ({ step, type }: PayrollPolicyHintProps) => {
  const { navigation } = useNavigation()
  if (type === 'fix') return null

  const trackLink = () => {
    track(trackingEvents.employee.contracts.goToPayrollPolicies, {
      step: step,
      type: type,
    })
  }

  return (
    <Hint color="grey200">
      <Href
        onClick={() => trackLink()}
        href={navigation.settings.payroll_bundle.fullPath()}
        target="_blank"
      >
        <Text.Normal color="grey1000" weight="normal" size="100" underlined>
          {i18n.t('contract_versions.flows.fix.steps.legal_and_extras.hints.payroll_policy')}
        </Text.Normal>
      </Href>
    </Hint>
  )
}

const PayrollPolicy = ({ translations, withFormWrapper, canEdit, step, type }: Props) => {
  const policies = usePolicies()
  const { contractVersion, contractVersionPolicy, setContractVersionPolicy } = useContractFlow()
  const hasPayrollPolicy = !!contractVersionPolicy
  const [showAssignModal, setShowAssignModal] = React.useState(false)

  const addDefaultPolicy = () => {
    const newPolicy = new ContractVersionPayrollPolicy({
      contract_version_id: contractVersion?.id,
      payroll_policy_id: policies.models[0]?.id,
    })
    setContractVersionPolicy(newPolicy)
  }

  const addPolicyFromList = (policyId: number | null) => {
    if (policyId) {
      const newPolicy = new ContractVersionPayrollPolicy({
        contract_version_id: contractVersion?.id,
        payroll_policy_id: policyId,
      })
      setContractVersionPolicy(newPolicy)
    }
    setShowAssignModal(false)
  }

  const onAddPayrollPolicy = () => {
    if (policies.length > 1) {
      setShowAssignModal(true)
    } else {
      addDefaultPolicy()
    }
  }

  const toolTipInformation = canEdit
    ? i18n.t('contract_versions.flows.fix.steps.legal_and_extras.payroll_policies.tooltip')
    : i18n.t(
        'contract_versions.flows.fix.steps.legal_and_extras.payroll_policies.no_editable_tooltip'
      )

  return (
    <Box gap="s8">
      <FormWrapper withFormWrapper={withFormWrapper}>
        <Box flexDirection="row" alignItems="center" gap="s2">
          <Text.Normal color="grey1000" weight="normal" size="200">
            {translate(translations, 'payroll_policy')}
          </Text.Normal>
          <InformationTooltip information={toolTipInformation} icon="info" />
        </Box>
      </FormWrapper>
      <FormWrapper
        withFormWrapper={withFormWrapper}
        hint={step && <PayrollPolicyHint step={step} type={type} />}
      >
        {hasPayrollPolicy && (
          <Item
            canEdit={canEdit}
            payrollPolicy={policies.get(contractVersionPolicy.get('payroll_policy_id'))!}
          />
        )}
        {!hasPayrollPolicy && (
          <Box
            paddingY="s16"
            paddingX="s32"
            border={{
              all: {
                width: 's1',
                color: 'grey400',
                style: 'solid',
              },
            }}
            borderRadius={{
              all: 'abs008',
            }}
            flexDirection="row"
            alignItems="center"
            justifyContent="spaceBetween"
          >
            <Text.Normal weight="medium" size="300" color="grey800">
              {translate(translations, 'no_payroll_policy')}
            </Text.Normal>
            {canEdit && (
              <Button type="secondary" onClick={() => onAddPayrollPolicy()} iconName="add" />
            )}
          </Box>
        )}
      </FormWrapper>
      {showAssignModal && (
        <AssignPolicyModal
          onClose={() => setShowAssignModal(false)}
          policies={policies.not_archived}
          addPolicy={(policyId) => addPolicyFromList(policyId)}
        />
      )}
    </Box>
  )
}

export default observer(PayrollPolicy)
